import { useMemo, useState } from 'react';
import { browserUuid } from '../domain/browser-crypto';
import { MAX_CINEMATIC_WAYPOINTS, type CinematicWaypoint, type CinematicOverrides } from '../../../renderer/src/cinematic-sequence';
import { moveSceneProperty, patchSceneProperty } from './timeline/scene-property-edit';
import { compileCinematicPlan, type CinematicSettings } from '../domain/cinematic-plan';
import type { Ride } from '../domain/ride';
import type { DraftControls } from './StudioSettings';
import { changeWaypointSetting } from './waypoint-settings';
import { upsertSceneKey } from './scene-keyframes';
import { moveTimelineWaypoint } from './timeline/timeline-waypoint-edit';
import { appearanceFields, presentationAppearance, withPresentationAppearance } from '../../../renderer/src/presentation-appearance';
import { LEGACY_PRESENTATION, type VideoPresentation } from '../../../renderer/src/video-presentation';
import { initializeStructuresView, structuresOverrides, STRUCTURES_TILT } from './structures-view';
import type { MapStyleId } from '../domain/map-style';
import { NIGHT_HAWK_TRACE_COLOR } from '../../../renderer/src/track-appearance';
import { blackbirdOverrides, initializeBlackbird } from './blackbird-preset';
import { resolveRenderPreset } from '../domain/render-preset';
import { deleteSceneKey } from './timeline/delete-scene-key';
import { inspectedSceneSettings } from './inspected-scene-settings';
import { routePadding } from '../map/camera-framing';
import { mapTrackDefault } from './map-track-default';

/** One playhead-driven inspector; explicit selection inspects the saved destination view. */
export function useCinematicWaypoints(ride: Ride, { draft, update }: DraftControls, duration: number,
  seek: (progress: number) => void, playing = false, presentation?: VideoPresentation) {
  const [inspection, setInspection] = useState<{ id: string | null; progress: number; property?: keyof CinematicOverrides | undefined } | null>(null);
  const [actionError, setActionError] = useState('');
  const points = ride.sourcePoints ?? ride.points;
  const output = resolveRenderPreset(draft.preset);
  const readoutViewport = { width: output.width, height: output.height, padding: routePadding(output.width, output.height), automaticPitchLimit: 60 };
  const { plan, error } = useMemo(() => {
    try { return { plan: compileCinematicPlan(points, { cameraStyleVersion: draft.cameraStyleVersion, cameraStyle: draft.camera, cameraGimbal: draft.cameraGimbal, cameraDistance: draft.distance,
      cameraAngle: draft.cameraAngle ?? undefined, mapStyle: draft.mapStyle as CinematicSettings['mapStyle'], depthOfField: draft.depthOfField,
      presentationAppearance: appearanceFields(presentation) },
    draft.cinematicSequence, readoutViewport, duration), error: '' }; }
    catch (cause) { return { plan: undefined, error: cause instanceof Error ? cause.message : 'The cinematic sequence could not be loaded.' }; }
  }, [points, draft.cameraStyleVersion, draft.camera, draft.distance, draft.cameraGimbal, draft.cameraAngle, draft.mapStyle, draft.depthOfField, draft.cinematicSequence, duration, presentation, output.width, output.height]);
  const timeline = plan?.waypoints ?? [];
  const live = draft.cinematicEnabled ? plan?.evaluate(draft.progress) : undefined;
  const activeTransition = Object.values(live?.transitions ?? {}).find(item => item.amount < 1);
  const frame = Math.round(draft.progress * (duration * 30 - 1));
  const keysAtPlayhead = timeline.filter(item => Math.round(item.seconds / duration * (duration * 30 - 1)) === frame).map(item => item.waypoint);
  const atPlayhead = keysAtPlayhead.find(item => item.id === inspection?.id) ?? keysAtPlayhead[0];
  const inspecting = !playing && inspection !== null && inspection.progress === draft.progress;
  const selectedId = draft.cinematicEnabled ? inspecting ? inspection.id : atPlayhead?.id ?? activeTransition?.id ?? live?.segment?.waypoint.id ?? null : null;
  const segment = plan?.segments.find(item => item.waypoint.id === selectedId);
  const selected = segment?.waypoint;
  const starting: CinematicSettings = { cameraStyle: draft.camera, cameraGimbal: draft.cameraGimbal, cameraDistance: draft.distance, cameraAngle: draft.cameraAngle,
    mapStyle: draft.mapStyle as CinematicSettings['mapStyle'], depthOfField: draft.depthOfField, presentationAppearance: appearanceFields(presentation) };
  const settings = inspecting ? inspection?.property && selected
    ? inspectedSceneSettings(live?.settings ?? starting, selected, inspection.property)
    : segment?.to ?? starting : live?.settings ?? starting;
  const transitioning = !inspecting && draft.cinematicEnabled && !!activeTransition;
  const readOnly = playing || !!error;
  const follow = () => { setInspection(null); setActionError(''); };
  const select = (id: string | null, property?: keyof CinematicOverrides) => {
    const item = timeline.find(item => item.waypoint.id === id);
    const progress = item ? item.seconds / duration : 0;
    setInspection(item ? { id: item.waypoint.id, progress, property } : null); setActionError(''); seek(progress);
  };
  const edit = (change: (current: typeof draft) => typeof draft) => {
    if (readOnly) return;
    seek(draft.progress); setInspection({ ...inspection, id: selectedId, progress: draft.progress }); setActionError(''); update(change);
  };
  const author = (overrides: CinematicWaypoint['overrides'], starting?: (current: typeof draft) => typeof draft,
    initialize?: (current: typeof draft, progress: number) => typeof draft) => {
    if (!plan || readOnly) return;
    try {
      const result = upsertSceneKey(points, draft.cinematicSequence, draft.progress, duration, browserUuid(), overrides);
      const property = Object.keys(overrides)[0] as keyof CinematicOverrides | undefined;
      seek(result.progress); setInspection({ id: result.key.id, progress: result.progress, property }); setActionError('');
      update(current => {
        const next = { ...(result.progress === 0 && starting ? starting(current) : current), cinematicEnabled: true, cameraStyleVersion: 4 as const,
          cinematicSequence: upsertSceneKey(points, current.cinematicSequence, result.progress, duration, result.authoredId, overrides).sequence };
        return initialize ? initialize(next, result.progress) : next;
      });
    } catch (cause) { setActionError(cause instanceof Error ? cause.message : 'The keyframe could not be saved.'); }
  };
  const set = <K extends keyof CinematicSettings>(key: K, value: CinematicSettings[K]) => {
    if (key === 'cameraStyle' && value === 'blackbird' && settings.cameraStyle !== value) {
      const pose = (inspecting ? segment?.toCamera : undefined)?.evaluate(draft.progress)
        ?? live?.camera ?? plan?.initialCamera.evaluate(draft.progress);
      if (!pose) return;
      author(blackbirdOverrides(), current => ({ ...current, camera: 'blackbird', mapStyle: 'blackbird' }),
        (current, progress) => initializeBlackbird(current, pose, settings, progress, duration));
      return;
    }
    author({ [key]: value }, current => changeWaypointSetting(current, null, key, value),
      key === 'mapStyle' ? current => mapTrackDefault(current, settings.mapStyle, value as string, settings.cameraStyle)
        : key === 'cameraStyle' && value === 'night-hawk' && settings.cameraStyle !== value
        ? current => ({ ...current, trackAppearance: { ...current.trackAppearance, color: NIGHT_HAWK_TRACE_COLOR } }) : undefined);
  };
  const selectMap = (mapStyle: MapStyleId) => {
    if (mapStyle !== 'relief') { set('mapStyle', mapStyle); return; }
    const pose = (inspecting ? segment?.toCamera : undefined)?.evaluate(draft.progress)
      ?? live?.camera ?? plan?.initialCamera.evaluate(draft.progress);
    if (!pose) return;
    author(structuresOverrides(), current => ({ ...current, mapStyle, distance: 'standard', cameraAngle: STRUCTURES_TILT }),
      (current, progress) => initializeStructuresView(current, pose, settings, progress, duration));
  };
  const inherit = (key: keyof CinematicSettings) => {
    const target = keysAtPlayhead.find(item => item.overrides[key] !== undefined);
    if (!target || readOnly) return;
    seek(draft.progress); setInspection({ id: target.id, progress: draft.progress }); setActionError('');
    update(current => ({ ...current, cinematicSequence: deleteSceneKey(current.cinematicSequence, target.id, key) }));
  };
  const patch = (value: Partial<CinematicWaypoint>, property = inspection?.property) => {
    if (selectedId && property && value.transition) {
      try {
        const result = patchSceneProperty(draft.cinematicSequence, selectedId, property, value.transition, browserUuid());
        edit(current => ({ ...current, cinematicSequence: result.sequence }));
        setInspection({ id: result.id, progress: draft.progress, property });
      } catch (cause) { setActionError(cause instanceof Error ? cause.message : 'The property key could not be saved.'); }
      return;
    }
    if (selectedId) edit(current => ({ ...current, cinematicSequence: { ...current.cinematicSequence,
      waypoints: current.cinematicSequence.waypoints.map(item => item.id === selectedId ? { ...item, ...value } : item) } }));
  };
  const add = () => {
    const property = inspection?.property ?? 'cameraStyle';
    const existing = keysAtPlayhead.find(item => item.overrides[property] !== undefined);
    if (existing) select(existing.id, property); else author({ [property]: settings[property] });
  };
  const move = (seconds: number) => {
    if (!selected || readOnly) return;
    if (!Number.isFinite(seconds) || seconds < 0 || seconds > duration) {
      setActionError(`Enter a time from 0 to ${duration.toFixed(2)} seconds.`); return;
    }
    try {
      const moved = inspection?.property ? moveSceneProperty(points, draft.cinematicSequence, selected.id, inspection.property, seconds, duration, browserUuid())
        : { ...moveTimelineWaypoint(points, draft.cinematicSequence, selected.id, seconds, duration), id: selected.id };
      const progress = moved.progress;
      update(current => ({ ...current, cinematicSequence: moved.sequence }));
      setInspection({ ...inspection, id: moved.id, progress }); setActionError(''); seek(progress);
    } catch { setActionError('A keyframe already occupies that position. Choose another time.'); }
  };
  const remove = () => {
    if (!atPlayhead || atPlayhead.id !== selectedId || readOnly) return;
    const id = atPlayhead.id;
    seek(draft.progress); follow();
    update(current => ({ ...current, cinematicSequence: deleteSceneKey(current.cinematicSequence, id, inspection?.property) }));
  };
  const previewSequence = useMemo(() => {
    if (!inspecting || selected?.transition.type !== 'smooth') return undefined;
    if (inspection?.property && Object.keys(selected.overrides).length > 1 && draft.cinematicSequence.waypoints.length >= MAX_CINEMATIC_WAYPOINTS) return undefined;
    if (inspection?.property && selected.overrides[inspection.property] !== undefined)
      return patchSceneProperty(draft.cinematicSequence, selected.id, inspection.property, { type: 'cut' }, browserUuid()).sequence;
    return { ...draft.cinematicSequence, waypoints: draft.cinematicSequence.waypoints.map(item => item.id === selectedId
      ? { ...item, transition: { type: 'cut' as const } } : item) };
  }, [inspecting, inspection?.property, selected, selectedId, draft.cinematicSequence]);
  const previewPlan = useMemo(() => {
    if (!plan) return plan;
    if (!draft.cinematicEnabled) return { ...plan, evaluate(progress: number) {
      return { camera: plan.initialCamera.evaluate(progress), settings: plan.initial, mapStyle: plan.initial.mapStyle,
        depthOfField: plan.initial.depthOfField, segment: undefined, amount: 1 };
    } };
    if (!previewSequence) return plan;
    return compileCinematicPlan(points,
      { ...plan.initial, cameraStyleVersion: draft.cameraStyleVersion, cameraAngle: plan.initial.cameraAngle ?? undefined },
      previewSequence, readoutViewport, duration);
  }, [previewSequence, plan, points, draft.cinematicEnabled, draft.cameraStyleVersion, output.width, output.height, duration]);
  return { timeline, error: error || actionError, selected, selectedId, selectedProperty: inspecting ? inspection?.property : undefined, atPlayhead, keysAtPlayhead,
    keyed: (property: keyof CinematicOverrides) => keysAtPlayhead.some(item => item.overrides[property] !== undefined), settings, inspecting, transitioning, readOnly,
    sceneAt: (seconds: number) => previewPlan?.evaluate(seconds / duration),
    camera: previewPlan?.evaluate(draft.progress).camera ?? { bearing: 0, pitch: 36 },
    live: transitioning && live ? { ...live, amount: activeTransition!.amount, segment: plan?.segments.find(item => item.waypoint.id === activeTransition!.id) ?? live.segment } : undefined,
    pitch: draft.cinematicEnabled ? live?.camera.pitch : plan?.initialCamera.evaluate(draft.progress).pitch,
    previewSequence, select, follow, patch, set, selectMap, inherit, add, move, remove,
    pause: () => { seek(draft.progress); setInspection(null); },
    keyframe: (key: keyof CinematicSettings) => key === 'cameraZoom' ? set(key, settings.cameraZoom ?? null) : key === 'presentationAppearance'
      ? set(key, presentationAppearance(withPresentationAppearance(presentation ?? LEGACY_PRESENTATION, settings.presentationAppearance)))
      : set(key, settings[key]),
    full: timeline.length >= MAX_CINEMATIC_WAYPOINTS };
}
export type CinematicEditing = ReturnType<typeof useCinematicWaypoints>;
